import { MINIAPP_PACKAGE_ID, APP_API_URL, APP_DRIVE_URL } from '@/constants';
import type { WardrobeItem } from '@/types';

import { getContext, isContextAvailable } from '../miniappContext';

import { addImageToDrive, ensureMyLooksFolderUid, onMyLooksFolderCreated } from './driveService';

interface PersistedWardrobeItem {
  id: string;
  name: string;
  category: string;
  thumbnailUrl: string;
  imageUrl: string;
}

export interface PersistedWardrobeData {
  version: 1;
  userId: string;
  updatedAt: string;
  items: PersistedWardrobeItem[];
}

const WARDROBE_ITEMS_FILE_NAME = 'ai-wardrobe-items.json';
const WARDROBE_ITEMS_FILE_SUBTYPE = 'ai-wardrobe-items';

let fileUidPromise: Promise<string | null> | null = null;
let saveChain: Promise<unknown> = Promise.resolve();
let deferredSave: { items: WardrobeItem[]; source: string } | null = null;
let isTriggeringFolderCreation = false;

onMyLooksFolderCreated(() => {
  isTriggeringFolderCreation = false;
  if (deferredSave) {
    const { items, source } = deferredSave;
    deferredSave = null;
    void saveWardrobeItemsToDrive(items, `deferred-from-${source}`);
  }
});

export function clearWardrobeItemsDriveCache(): void {
  fileUidPromise = null;
}

function getCurrentUserId(): string {
  if (!isContextAvailable()) return 'unknown';
  const state = getContext().general.auth.getState() as { userId?: string; uid?: string };
  return state.userId ?? state.uid ?? 'unknown';
}

function isPersistedUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://');
}

function toPersistedItem(item: WardrobeItem): PersistedWardrobeItem | null {
  if (!isPersistedUrl(item.imageUrl)) return null;
  return {
    id: item.id,
    name: item.name,
    category: item.category,
    thumbnailUrl: isPersistedUrl(item.thumbnailUrl) ? item.thumbnailUrl : item.imageUrl,
    imageUrl: item.imageUrl,
  };
}

function toWardrobeItem(p: PersistedWardrobeItem): WardrobeItem {
  return {
    id: p.id,
    name: p.name,
    category: p.category as WardrobeItem['category'],
    thumbnailUrl: p.thumbnailUrl,
    imageUrl: p.imageUrl,
    isUserUploaded: true,
    isProcessing: false,
  };
}

function getWardrobeItemsFileUid(): Promise<string | null> {
  if (!fileUidPromise) {
    fileUidPromise = (async () => {
      try {
        const folderId = await ensureMyLooksFolderUid();
        if (!folderId) return null;
        const ctx = getContext();
        const url = new URL(`${APP_API_URL}/cloud-storage/v1/me/files`);
        url.searchParams.set('fileTypes', 'FILE');
        url.searchParams.set('parentFolderUid', folderId);
        const response = await ctx.utils.api.fetch(url.toString());
        if (!response.ok) return null;
        const data = await response.json();
        const files: Array<{ uid: string; name: string }> = data.response ?? [];
        return files.find(f => f.name === WARDROBE_ITEMS_FILE_NAME)?.uid ?? null;
      } catch {
        fileUidPromise = null;
        return null;
      }
    })();
  }
  return fileUidPromise;
}

async function buildFormData(json: string, folderId: string): Promise<FormData> {
  const metadata: Record<string, unknown> = {
    name: WARDROBE_ITEMS_FILE_NAME,
    attributes: { tool: MINIAPP_PACKAGE_ID, subType: WARDROBE_ITEMS_FILE_SUBTYPE },
    parentFolderId: folderId,
    hidden: true,
  };
  const fd = new FormData();
  fd.append('file', new File([json], WARDROBE_ITEMS_FILE_NAME, { type: 'application/json' }));
  fd.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  return fd;
}

async function doSave(items: WardrobeItem[], source: string): Promise<boolean> {
  try {
    const ctx = getContext();

    const [existingFileId, folderId] = await Promise.all([getWardrobeItemsFileUid(), ensureMyLooksFolderUid()]);

    if (!folderId) {
      deferredSave = { items, source };

      if (!isTriggeringFolderCreation) {
        const cdnItem = items.find(item => isPersistedUrl(item.imageUrl));
        if (cdnItem) {
          isTriggeringFolderCreation = true;
          void addImageToDrive({ imageUrl: cdnItem.imageUrl });
        }
      }

      return true;
    }

    const persistedItems: PersistedWardrobeItem[] = [];
    for (const item of items) {
      const p = toPersistedItem(item);
      if (p) persistedItems.push(p);
    }

    const data: PersistedWardrobeData = {
      version: 1,
      userId: getCurrentUserId(),
      updatedAt: new Date().toISOString(),
      items: persistedItems,
    };
    const json = JSON.stringify(data);

    if (existingFileId) {
      const response = await ctx.utils.api.fetch(`${APP_DRIVE_URL}/drive/v1/files/${existingFileId}`, {
        method: 'PATCH',
        body: await buildFormData(json, folderId),
      });
      if (response.ok) {
        return true;
      }
      fileUidPromise = null;
    }

    const response = await ctx.utils.api.fetch(`${APP_DRIVE_URL}/drive/v1/files`, {
      method: 'POST',
      body: await buildFormData(json, folderId),
    });

    if (response.ok) {
      fileUidPromise = null; // reset so next save re-queries if UID not in response
      try {
        const result = await response.json();
        const newId: string | undefined = result.response?.file?.uid;
        if (newId) fileUidPromise = Promise.resolve(newId);
      } catch {
        // empty body — fileUidPromise stays null, will re-query on next save
      }
    }

    return response.ok;
  } catch {
    return false;
  }
}

export const saveWardrobeItemsToDrive = (items: WardrobeItem[], source: string): Promise<boolean> => {
  if (!isContextAvailable()) {
    return Promise.resolve(false);
  }
  const next = saveChain.catch(() => {}).then(() => doSave(items, source));
  saveChain = next.catch(() => {});
  return next;
};

export const loadWardrobeItemsFromDrive = async (): Promise<WardrobeItem[] | null> => {
  if (!isContextAvailable()) return null;
  try {
    const fileId = await getWardrobeItemsFileUid();
    if (!fileId) {
      return null;
    }
    const ctx = getContext();
    const response = await ctx.utils.api.fetch(`${APP_DRIVE_URL}/drive/v1/files/${fileId}/download`);
    if (response.status === 404) {
      fileUidPromise = null;
      return null;
    }
    if (!response.ok) return null;
    const text = await response.text();
    const parsed: unknown = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || !('items' in parsed)) return null;
    const data = parsed as PersistedWardrobeData;
    return Array.isArray(data.items) ? data.items.map(toWardrobeItem) : null;
  } catch {
    return null;
  }
};
